import { useEffect, useRef, useCallback } from 'react';

interface UseSmartPollingOptions {
  /**
   * Polling interval in milliseconds
   */
  pollInterval: number;

  /**
   * Whether polling is enabled
   */
  enabled?: boolean;

  /**
   * Function to call on each poll
   */
  onPoll: () => void | Promise<void>;

  /**
   * Function to generate a comparison key
   * Only calls onPoll if the key changes
   */
  compareKey?: () => string | number;

  /**
   * Whether to poll immediately on mount
   */
  pollOnMount?: boolean;
}

/**
 * Smart polling hook with change detection
 * Only polls when data actually changes (based on compareKey)
 * Matches mobile app implementation
 */
export function useSmartPolling({
  pollInterval,
  enabled = true,
  onPoll,
  compareKey,
  pollOnMount = false,
}: UseSmartPollingOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastKeyRef = useRef<string | number | null>(null);
  const isPollingRef = useRef(false);

  /**
   * Execute poll with change detection
   */
  const executePoll = useCallback(async () => {
    // Skip if polling is already in progress
    if (isPollingRef.current) {
      console.log('[useSmartPolling] Poll already in progress, skipping');
      return;
    }

    // Check if data has changed (if compareKey is provided)
    if (compareKey) {
      const currentKey = compareKey();

      if (lastKeyRef.current !== null && lastKeyRef.current === currentKey) {
        console.log('[useSmartPolling] No changes detected, skipping poll');
        return;
      }

      console.log('[useSmartPolling] Change detected, executing poll', {
        oldKey: lastKeyRef.current,
        newKey: currentKey,
      });

      lastKeyRef.current = currentKey;
    }

    // Execute poll
    try {
      isPollingRef.current = true;
      await onPoll();
    } catch (error) {
      console.error('[useSmartPolling] Error during poll:', error);
    } finally {
      isPollingRef.current = false;
    }
  }, [onPoll, compareKey]);

  /**
   * Start polling
   */
  const startPolling = useCallback(() => {
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up new interval
    intervalRef.current = setInterval(() => {
      executePoll();
    }, pollInterval);

    console.log('[useSmartPolling] Polling started', { interval: pollInterval });
  }, [pollInterval, executePoll]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log('[useSmartPolling] Polling stopped');
    }
  }, []);

  /**
   * Set up polling effect
   */
  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }

    // Poll immediately on mount if requested
    if (pollOnMount) {
      executePoll();
    }

    // Start polling interval
    startPolling();

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [enabled, pollOnMount, startPolling, stopPolling, executePoll]);

  /**
   * Handle visibility change (pause when tab is hidden)
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[useSmartPolling] Tab hidden, pausing polling');
        stopPolling();
      } else {
        console.log('[useSmartPolling] Tab visible, resuming polling');
        if (enabled) {
          // Poll immediately when tab becomes visible
          executePoll();
          startPolling();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, executePoll, startPolling, stopPolling]);

  return {
    startPolling,
    stopPolling,
    executePoll,
  };
}
